/**
 * Terms Management Service - Frontend API calls for terms operations
 * Uses shared API client from @/shared/api
 */

import api from "@/shared/api";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * Get current published terms
 * @returns {Promise<Object>} Current published terms
 */
export const getCurrentTerms = async () => {
  try {
    const response = await api.get("/terms/quote-link/current");
    logInfo("Current terms fetched successfully");
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to fetch current terms", {
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Publish new terms
 * @param {Object} termsData - Terms data
 * @param {string[]} termsData.terms - Array of service terms
 * @param {string[]} termsData.privacyPolicy - Array of privacy policy items
 * @param {string} termsData.callNumber - Contact phone number
 * @param {string} termsData.contactEmail - Contact email
 * @param {string} [termsData.notes] - Optional notes
 * @returns {Promise<Object>} Publish result
 */
export const publishTerms = async (termsData) => {
  try {
    const response = await api.post("/terms/quote-link/publish", termsData);
    logInfo("Terms published successfully");
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to publish terms", {
      termsData,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get terms version history
 * @param {Object} options - Query options
 * @param {boolean} [options.includeUnpublished] - Include unpublished versions
 * @param {number} [options.limit] - Limit results
 * @returns {Promise<Object>} Terms versions list
 */
export const getTermsVersions = async (options = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (options.includeUnpublished) {
      queryParams.append("includeUnpublished", "true");
    }
    if (options.limit) {
      queryParams.append("limit", String(options.limit));
    }

    const queryString = queryParams.toString();
    const path = `/terms/quote-link/versions${queryString ? `?${queryString}` : ""}`;
    const response = await api.get(path);
    logInfo("Terms versions fetched successfully");
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to fetch terms versions", {
      options,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get terms document by ID
 * @param {string} id - Terms document ID
 * @returns {Promise<Object>} Terms document
 */
export const getTermsById = async (id) => {
  try {
    const response = await api.get(`/terms/quote-link/${id}`);
    logInfo("Terms document fetched successfully", { id });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to fetch terms document", {
      id,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};
